import { Button, Card, Result, Spin } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { AntPageLayoutWithHeader } from 'client/modules/permissions/components/AntPageLayoutWithHeader';
import { getRpcError } from 'client/modules/common/utils/rpcError';
import { useGetDataFeedConfig } from '../services/hooks';
import { EditDataFeedTabs } from '../components/EditDataFeedTabs';
import { formatMB } from '../components/JSONRequestFilters';
import { DataFeedRoutes } from '../routesHelper';

const classifyError = (error: unknown) => {
  const { code, status, message } = getRpcError(error);
  const sizeMatch = message.match(/\((\d+)\s*vs\.?\s*(\d+)\)/);
  return {
    // gRPC RESOURCE_EXHAUSTED (8): saved API request exceeds the BFF 100 MB receive limit.
    tooLarge: code === 8 || /larger than max|RESOURCE_EXHAUSTED/i.test(message),
    notFound: code === 5 || status === 404,
    payloadBytes: sizeMatch ? Number(sizeMatch[1]) : undefined,
  };
};

export const EditDataFeed = () => {
  const { idTeam, idDataFeed } = useParams<{
    idTeam: string;
    idDataFeed: string;
  }>();
  const { data, isFetching, isError, error } = useGetDataFeedConfig(
    Number(idDataFeed)
  );

  const backToList = (
    <Link to={`${DataFeedRoutes.DataFeed}/${idTeam}`}>
      <Button type="primary">Back to data feeds</Button>
    </Link>
  );

  if (!idTeam || !idDataFeed || Number.isNaN(Number(idDataFeed))) {
    return <Result status="404" title="Data feed not found" />;
  }

  if (isFetching) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (isError) {
    const { tooLarge, notFound, payloadBytes } = classifyError(error);
    if (notFound) {
      return (
        <Result status="404" title="Data feed not found" extra={backToList} />
      );
    }
    if (tooLarge) {
      const size = payloadBytes ? ` (${formatMB(payloadBytes)})` : '';
      return (
        <Result
          status="warning"
          title="This data feed is too large to open here"
          subTitle={`Its saved API request ${size} is larger than the 100 MB this page can load, so it can't be opened for editing.`}
          extra={backToList}
        />
      );
    }
    return (
      <Result
        status="error"
        title="Couldn't load this data feed"
        subTitle="Something went wrong while loading it. Please try again."
        extra={backToList}
      />
    );
  }

  const feed = data?.data_feed_config;
  if (!feed) {
    return (
      <Result status="404" title="Data feed not found" extra={backToList} />
    );
  }

  return (
    <AntPageLayoutWithHeader title={`Edit ${feed.data_feed_name}`}>
      <Card className="w-full" title="Edit data feed">
        <EditDataFeedTabs feed={feed} idTeam={idTeam} />
      </Card>
    </AntPageLayoutWithHeader>
  );
};
